import React, { useCallback, useEffect, useMemo, useState } from 'react';
import venueSettingsService, { CuisineType } from '../../services/venueSettingsService';
import toastService from '../../services/toastService';
import StatTile from '../Dashboard/StatTile';
import { CalendarIcon, DollarIcon, MenuIcon, StadiumIcon } from '../UI/Icons';
import ChipEditor from './ChipEditor';
import CuisineEditor from './CuisineEditor';
import styles from './VenueSettings.module.css';

export interface VenueFormSnapshot {
  name: string;
  brandColor: string;
  taxRatePercent: number;
  serviceFee: number;
  kickoffAt: string;
  walkSecPerSection: number;
  basePrepMin: number;
  sections: string[];
  cuisineTypes: CuisineType[];
}

interface ValidationErrors {
  name?: string;
  taxRatePercent?: string;
  serviceFee?: string;
  walkSecPerSection?: string;
  basePrepMin?: string;
  cuisineIds?: Record<number, string>;
}

const DEFAULT_BRAND = '#B10202';

function normalizeCuisineTypes(types: CuisineType[]): CuisineType[] {
  return types.map((t) => ({
    id: t.id.trim(),
    label: (t.label || t.id).trim(),
    icon: t.icon || '🍽️',
  }));
}

function snapshotFromPayload(data: {
  name?: string;
  brandColor?: string;
  taxRate?: number;
  serviceFee?: number;
  kickoffAt?: string | null;
  walkSecPerSection?: number;
  basePrepMin?: number;
  sections?: string[] | null;
  cuisineTypes?: CuisineType[] | null;
}): VenueFormSnapshot {
  return {
    name: data.name || '',
    brandColor: data.brandColor || DEFAULT_BRAND,
    taxRatePercent: Math.round((data.taxRate ?? 0.08) * 10000) / 100,
    serviceFee: data.serviceFee ?? 2.5,
    kickoffAt: data.kickoffAt || '',
    walkSecPerSection: data.walkSecPerSection ?? 30,
    basePrepMin: data.basePrepMin ?? 4,
    sections: [...(data.sections || [])],
    cuisineTypes: normalizeCuisineTypes(data.cuisineTypes || []),
  };
}

function formatRelativeKickoff(kickoffAt: string): string {
  if (!kickoffAt) return 'Not set';
  const date = new Date(kickoffAt);
  if (Number.isNaN(date.getTime())) return 'Not set';

  const now = new Date();
  const diffMs = date.getTime() - now.getTime();
  const isPast = diffMs < 0;
  const absDiff = Math.abs(diffMs);

  const minutes = Math.floor(absDiff / 60000);
  const hours = Math.floor(absDiff / 3600000);
  const days = Math.floor(absDiff / 86400000);

  if (days > 0) {
    return isPast
      ? `Kickoff was ${days} day${days !== 1 ? 's' : ''} ago`
      : `In ${days} day${days !== 1 ? 's' : ''}`;
  }
  if (hours > 0) {
    return isPast ? `Kickoff was ${hours}h ago` : `In ${hours}h`;
  }
  if (minutes > 0) {
    return isPast ? `Kickoff was ${minutes}m ago` : `In ${minutes}m`;
  }
  return isPast ? 'Kickoff just passed' : 'Starting soon';
}

function countDirtyFields(current: VenueFormSnapshot, baseline: VenueFormSnapshot): number {
  let count = 0;
  if (current.name !== baseline.name) count += 1;
  if (current.brandColor !== baseline.brandColor) count += 1;
  if (current.taxRatePercent !== baseline.taxRatePercent) count += 1;
  if (current.serviceFee !== baseline.serviceFee) count += 1;
  if (current.kickoffAt !== baseline.kickoffAt) count += 1;
  if (current.walkSecPerSection !== baseline.walkSecPerSection) count += 1;
  if (current.basePrepMin !== baseline.basePrepMin) count += 1;
  if (JSON.stringify(current.sections) !== JSON.stringify(baseline.sections)) count += 1;
  if (JSON.stringify(current.cuisineTypes) !== JSON.stringify(baseline.cuisineTypes)) count += 1;
  return count;
}

function VenueSettingsSkeleton() {
  return (
    <div className={styles.page} aria-hidden="true">
      <div className={`${styles.skeletonBlock} ${styles.skeletonTitle}`} />
      <div className={`${styles.skeletonBlock} ${styles.skeletonSubtitle}`} />
      <div className={styles.skeletonLayout}>
        <div className={styles.skeletonMain}>
          {Array.from({ length: 4 }).map((_, index) => (
            <div key={index} className={styles.skeletonCard}>
              <div className={`${styles.skeletonBlock} ${styles.skeletonCardTitle}`} />
              <div className={`${styles.skeletonBlock} ${styles.skeletonField}`} />
              <div className={`${styles.skeletonBlock} ${styles.skeletonField}`} />
            </div>
          ))}
        </div>
        <div className={styles.skeletonRail}>
          <div className={`${styles.skeletonBlock} ${styles.skeletonRailCard}`} />
          <div className={`${styles.skeletonBlock} ${styles.skeletonRailCard}`} />
        </div>
      </div>
    </div>
  );
}

const VenueSettings: React.FC = () => {
  const [form, setForm] = useState<VenueFormSnapshot>(() => snapshotFromPayload({}));
  const [loadedSnapshot, setLoadedSnapshot] = useState<VenueFormSnapshot>(() => snapshotFromPayload({}));
  const [loadedAt, setLoadedAt] = useState<Date | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<ValidationErrors>({});

  const loadSettings = useCallback(async () => {
    try {
      setLoading(true);
      const data = await venueSettingsService.get();
      const snapshot = snapshotFromPayload(data);
      setForm(snapshot);
      setLoadedSnapshot(snapshot);
      setLoadedAt(new Date());
      setErrors({});
    } catch {
      toastService.error('Failed to load venue settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const dirtyCount = useMemo(() => countDirtyFields(form, loadedSnapshot), [form, loadedSnapshot]);
  const dirty = dirtyCount > 0;

  const kickoffRelative = useMemo(() => formatRelativeKickoff(form.kickoffAt), [form.kickoffAt]);

  const validate = (): ValidationErrors => {
    const next: ValidationErrors = {};
    if (!form.name.trim()) next.name = 'Venue name is required';
    if (form.taxRatePercent < 0 || form.taxRatePercent > 100) {
      next.taxRatePercent = 'Tax rate must be between 0 and 100%';
    }
    if (form.serviceFee < 0) next.serviceFee = 'Service fee must be 0 or greater';
    if (form.walkSecPerSection < 1) next.walkSecPerSection = 'Walk seconds must be at least 1';
    if (form.basePrepMin < 0) next.basePrepMin = 'Base prep must be 0 or greater';

    const cuisineIds: Record<number, string> = {};
    const seen = new Set<string>();
    form.cuisineTypes.forEach((row, index) => {
      if (!row.id.trim()) {
        cuisineIds[index] = 'ID is required';
      } else if (seen.has(row.id)) {
        cuisineIds[index] = 'Duplicate ID';
      } else {
        seen.add(row.id);
      }
    });
    if (Object.keys(cuisineIds).length > 0) next.cuisineIds = cuisineIds;

    return next;
  };

  const handleDiscard = () => {
    setForm({ ...loadedSnapshot, sections: [...loadedSnapshot.sections], cuisineTypes: [...loadedSnapshot.cuisineTypes] });
    setErrors({});
  };

  const handleSave = async () => {
    const validation = validate();
    setErrors(validation);
    const errorCount =
      Object.keys(validation).filter((k) => k !== 'cuisineIds').length +
      Object.keys(validation.cuisineIds || {}).length;

    if (errorCount > 0) {
      toastService.error(`Fix ${errorCount} invalid field${errorCount !== 1 ? 's' : ''}`);
      return;
    }

    const normalizedCuisine = form.cuisineTypes.map((row) => ({
      id: row.id.trim(),
      label: (row.label.trim() || row.id.trim()),
      icon: row.icon || '🍽️',
    }));

    setSaving(true);
    try {
      const updated = await venueSettingsService.update({
        name: form.name.trim(),
        brandColor: form.brandColor,
        taxRate: form.taxRatePercent / 100,
        serviceFee: form.serviceFee,
        kickoffAt: form.kickoffAt || null,
        walkSecPerSection: form.walkSecPerSection,
        basePrepMin: form.basePrepMin,
        sections: form.sections,
        cuisineTypes: normalizedCuisine,
      });

      const snapshot = snapshotFromPayload(updated);
      setForm(snapshot);
      setLoadedSnapshot(snapshot);
      setLoadedAt(new Date());
      setErrors({});
      toastService.success('Venue settings saved. Customer app will reflect changes on next load.');
    } catch {
      toastService.error('Failed to save venue settings');
    } finally {
      setSaving(false);
    }
  };

  const updateForm = <K extends keyof VenueFormSnapshot>(key: K, value: VenueFormSnapshot[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => {
      const next = { ...prev };
      if (key === 'name') delete next.name;
      if (key === 'taxRatePercent') delete next.taxRatePercent;
      if (key === 'serviceFee') delete next.serviceFee;
      if (key === 'walkSecPerSection') delete next.walkSecPerSection;
      if (key === 'basePrepMin') delete next.basePrepMin;
      if (key === 'cuisineTypes') delete next.cuisineIds;
      return next;
    });
  };

  if (loading) return <VenueSettingsSkeleton />;

  const previewName = form.name.trim() || 'Your Venue';

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div>
          <h1 className={styles.title}>Venue Settings</h1>
          <p className={styles.subtitle}>Branding, pricing, game day, and stadium taxonomy.</p>
        </div>
        {dirty && (
          <span className={styles.dirtyBadge}>
            {dirtyCount} unsaved change{dirtyCount !== 1 ? 's' : ''}
          </span>
        )}
      </header>

      <div className={styles.statTileRow}>
        <StatTile icon={<MenuIcon size={16} />} label="Cuisine Types" value={form.cuisineTypes.length} />
        <StatTile icon={<StadiumIcon size={16} />} label="Sections" value={form.sections.length} />
        <StatTile
          icon={<DollarIcon size={16} />}
          label="Tax Rate"
          value={`${form.taxRatePercent.toFixed(2)}%`}
        />
        <StatTile icon={<CalendarIcon size={16} />} label="Game Day" value={kickoffRelative} />
      </div>

      <div className={styles.layout}>
        <div className={styles.mainColumn}>
          <section className={styles.card}>
            <h2 className={styles.cardTitle}>Branding</h2>
            <div className={styles.cardBody}>
              <div className={styles.field}>
                <label className={styles.label} htmlFor="venue-name">Venue name</label>
                <input
                  id="venue-name"
                  className={`${styles.input} ${errors.name ? styles.inputError : ''}`}
                  value={form.name}
                  onChange={(e) => updateForm('name', e.target.value)}
                />
                {errors.name && <p className={styles.fieldError}>{errors.name}</p>}
              </div>

              <div className={styles.field}>
                <label className={styles.label} htmlFor="venue-color">Brand color</label>
                <div className={styles.colorRow}>
                  <input
                    id="venue-color"
                    type="color"
                    className={styles.colorInput}
                    value={form.brandColor}
                    onChange={(e) => updateForm('brandColor', e.target.value)}
                  />
                  <span className={styles.colorHex}>{form.brandColor.toUpperCase()}</span>
                  <span
                    className={styles.brandSwatchInline}
                    style={{ backgroundColor: form.brandColor }}
                    aria-hidden="true"
                  />
                </div>
              </div>

              <div className={styles.brandPreviewInline}>
                <span className={styles.brandPreviewLabel}>Preview</span>
                <span
                  className={styles.brandPreviewChip}
                  style={{ backgroundColor: form.brandColor }}
                >
                  {previewName}
                </span>
              </div>
            </div>
          </section>

          <section className={styles.card}>
            <h2 className={styles.cardTitle}>Pricing</h2>
            <div className={styles.cardBody}>
              <div className={styles.fieldRow}>
                <div className={styles.field}>
                  <label className={styles.label} htmlFor="tax-rate">Tax rate</label>
                  <div className={styles.percentInputWrap}>
                    <input
                      id="tax-rate"
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      className={`${styles.input} ${styles.percentInput} ${errors.taxRatePercent ? styles.inputError : ''}`}
                      value={form.taxRatePercent}
                      onChange={(e) => updateForm('taxRatePercent', parseFloat(e.target.value) || 0)}
                    />
                    <span className={styles.percentSuffix} aria-hidden="true">%</span>
                  </div>
                  {errors.taxRatePercent && <p className={styles.fieldError}>{errors.taxRatePercent}</p>}
                  <p className={styles.fieldHint}>Stored internally as a decimal ({(form.taxRatePercent / 100).toFixed(4)})</p>
                </div>

                <div className={styles.field}>
                  <label className={styles.label} htmlFor="service-fee">Service fee</label>
                  <div className={styles.dollarInputWrap}>
                    <span className={styles.dollarPrefix} aria-hidden="true">$</span>
                    <input
                      id="service-fee"
                      type="number"
                      step="0.01"
                      min="0"
                      className={`${styles.input} ${styles.dollarInput} ${errors.serviceFee ? styles.inputError : ''}`}
                      value={form.serviceFee}
                      onChange={(e) => updateForm('serviceFee', parseFloat(e.target.value) || 0)}
                    />
                  </div>
                  {errors.serviceFee && <p className={styles.fieldError}>{errors.serviceFee}</p>}
                </div>
              </div>
            </div>
          </section>

          <section className={styles.card}>
            <h2 className={styles.cardTitle}>Game Day</h2>
            <div className={styles.cardBody}>
              <div className={styles.field}>
                <label className={styles.label} htmlFor="kickoff">Kickoff</label>
                <div className={styles.kickoffRow}>
                  <input
                    id="kickoff"
                    type="datetime-local"
                    className={styles.input}
                    value={form.kickoffAt ? form.kickoffAt.slice(0, 16) : ''}
                    onChange={(e) =>
                      updateForm(
                        'kickoffAt',
                        e.target.value ? new Date(e.target.value).toISOString() : '',
                      )
                    }
                  />
                  <button
                    type="button"
                    className={styles.clearButton}
                    onClick={() => updateForm('kickoffAt', '')}
                    disabled={!form.kickoffAt}
                  >
                    Clear
                  </button>
                </div>
                <p className={styles.fieldHint}>{kickoffRelative}</p>
              </div>

              <div className={styles.fieldRow}>
                <div className={styles.field}>
                  <label className={styles.label} htmlFor="walk-sec">Walk seconds per section</label>
                  <input
                    id="walk-sec"
                    type="number"
                    min="1"
                    className={`${styles.input} ${errors.walkSecPerSection ? styles.inputError : ''}`}
                    value={form.walkSecPerSection}
                    onChange={(e) => updateForm('walkSecPerSection', parseInt(e.target.value, 10) || 1)}
                  />
                  {errors.walkSecPerSection && (
                    <p className={styles.fieldError}>{errors.walkSecPerSection}</p>
                  )}
                </div>
                <div className={styles.field}>
                  <label className={styles.label} htmlFor="base-prep">Base prep minutes</label>
                  <input
                    id="base-prep"
                    type="number"
                    min="0"
                    className={`${styles.input} ${errors.basePrepMin ? styles.inputError : ''}`}
                    value={form.basePrepMin}
                    onChange={(e) => updateForm('basePrepMin', parseInt(e.target.value, 10) || 0)}
                  />
                  {errors.basePrepMin && <p className={styles.fieldError}>{errors.basePrepMin}</p>}
                </div>
              </div>
            </div>
          </section>

          <section className={styles.card}>
            <h2 className={styles.cardTitle}>Stadium Taxonomy</h2>
            <div className={styles.cardBody}>
              <div className={styles.subSection}>
                <h3 className={styles.subSectionTitle}>Sections</h3>
                <ChipEditor
                  value={form.sections}
                  onChange={(sections) => updateForm('sections', sections)}
                  ariaLabel="Sections"
                  allowRanges
                />
              </div>
              <div className={styles.subSection}>
                <h3 className={styles.subSectionTitle}>Cuisine Types</h3>
                <CuisineEditor
                  value={form.cuisineTypes}
                  onChange={(cuisineTypes) => updateForm('cuisineTypes', cuisineTypes)}
                  idErrors={errors.cuisineIds}
                />
              </div>
            </div>
          </section>
        </div>

        <aside className={styles.rightRail}>
          <div className={styles.railCard}>
            <h3 className={styles.railTitle}>Brand Preview</h3>
            <div className={styles.brandPreview}>
              <span
                className={styles.brandPreviewChipLarge}
                style={{ backgroundColor: form.brandColor }}
              >
                {previewName}
              </span>
              <div className={styles.brandMeta}>
                <span
                  className={styles.brandSwatch}
                  style={{ backgroundColor: form.brandColor }}
                  aria-hidden="true"
                />
                <span className={styles.brandHex}>{form.brandColor.toUpperCase()}</span>
              </div>
            </div>
          </div>

          <div className={styles.railCard}>
            <h3 className={styles.railTitle}>Summary</h3>
            <dl className={styles.summaryList}>
              <div className={styles.summaryItem}>
                <dt>Cuisine types</dt>
                <dd>{form.cuisineTypes.length}</dd>
              </div>
              <div className={styles.summaryItem}>
                <dt>Sections</dt>
                <dd>{form.sections.length}</dd>
              </div>
              <div className={styles.summaryItem}>
                <dt>Tax rate</dt>
                <dd>{form.taxRatePercent.toFixed(2)}%</dd>
              </div>
              <div className={styles.summaryItem}>
                <dt>Kickoff</dt>
                <dd>{kickoffRelative}</dd>
              </div>
              {loadedAt && (
                <div className={styles.summaryItem}>
                  <dt>Last loaded</dt>
                  <dd>{loadedAt.toLocaleString()}</dd>
                </div>
              )}
            </dl>
          </div>

          <div className={styles.saveBar}>
            <p className={styles.saveStatus} aria-live="polite">
              {dirty
                ? `${dirtyCount} unsaved change${dirtyCount !== 1 ? 's' : ''}`
                : 'All changes saved'}
            </p>
            <div className={styles.saveActions}>
              <button
                type="button"
                className={styles.saveSecondary}
                onClick={handleDiscard}
                disabled={!dirty || saving}
              >
                Discard
              </button>
              <button
                type="button"
                className={styles.savePrimary}
                onClick={handleSave}
                disabled={!dirty || saving}
              >
                {saving ? 'Saving…' : 'Save settings'}
              </button>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
};

export default VenueSettings;
